import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileArrowUp, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";

const defaultForm = {
  client_id: "", client_name: "", application_number: "", funding_year: "",
  category_of_service: "category_1", services_requested: "", posting_date: "",
  allowable_contract_date: "", status: "open", bid_count: 0, notes: "",
};
const statusColors = {
  open: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  closed: "bg-gray-100 text-[#6B7280] border-gray-200",
  certified: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
};

export default function Form470() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });

  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);
  const csvCols = [
    { label: "App #", key: "application_number" }, { label: "Client", key: "client_name" }, { label: "FY", key: "funding_year" },
    { label: "Category", key: "category_of_service" }, { label: "Services", key: "services_requested" }, { label: "Posted", key: "posting_date" },
    { label: "Status", key: "status" },
  ];
  const SH = ({ k, children }) => (
    <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20]" onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load(); loadClients(); }, []);
  const load = async () => { try { const { data } = await api.get("/forms/470"); setItems(data); } catch { toast.error("Failed to load"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const save = async () => {
    try {
      if (editing) { await api.put(`/forms/470/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/forms/470", form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/forms/470/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => { const c = clients.find((cl) => cl.id === id); setForm((f) => ({ ...f, client_id: id, client_name: c ? c.name : "" })); };

  return (
    <div className="p-6" data-testid="form470-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">E-Rate Forms</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>FCC Form 470</h1>
          <p className="text-sm text-[#6B7280] mt-1">Description of Services Requested and Certification</p>
        </div>
        <button data-testid="add-form470-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} weight="bold" /> Add Form 470
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-form470" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger className="rounded-none w-[120px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All</SelectItem><SelectItem value="open">Open</SelectItem><SelectItem value="closed">Closed</SelectItem><SelectItem value="certified">Certified</SelectItem></SelectContent>
        </Select>
        <Select value={filters.category_of_service || "all"} onValueChange={(v) => setFilter("category_of_service", v)}>
          <SelectTrigger className="rounded-none w-[130px]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All</SelectItem><SelectItem value="category_1">Cat 1</SelectItem><SelectItem value="category_2">Cat 2</SelectItem></SelectContent>
        </Select>
        <Select value={filters.funding_year || "all"} onValueChange={(v) => setFilter("funding_year", v)}>
          <SelectTrigger data-testid="filter-470-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {[...new Set(items.map((i) => i.funding_year).filter(Boolean))].sort().reverse().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear</button>}
        <button data-testid="export-form470-btn" onClick={() => exportCSV(processed, csvCols, "form470.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <SH k="application_number">App #</SH>
              <SH k="client_name">Client</SH>
              <SH k="funding_year">FY</SH>
              <SH k="category_of_service">Category</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Services</TableHead>
              <SH k="posting_date">Posted</SH>
              <SH k="status">Status</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B7280] text-sm">No Form 470 records</TableCell></TableRow>
            ) : processed.map((item) => (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-4 font-mono text-xs text-[#1A1D20]">{item.application_number || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.client_name || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.funding_year || "—"}</TableCell>
                <TableCell className="py-3 px-4"><Badge variant="outline" className="rounded-none text-xs">{item.category_of_service === "category_1" ? "Cat 1" : "Cat 2"}</Badge></TableCell>
                <TableCell className="py-3 px-4 text-sm text-[#6B7280] max-w-[150px] truncate">{item.services_requested || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.posting_date || "—"}</TableCell>
                <TableCell className="py-3 px-4"><Badge className={`rounded-none text-xs border capitalize ${statusColors[item.status] || statusColors.open}`}>{item.status}</Badge></TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-form470-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                    <button data-testid={`delete-form470-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Form 470</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client</label>
                <Select value={form.client_id} onValueChange={selectClient}>
                  <SelectTrigger data-testid="form470-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-[200px]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Application Number</label>
                <Input data-testid="form470-appnum-input" value={form.application_number} onChange={(e) => set("application_number", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Funding Year</label>
                <Input data-testid="form470-fy-input" value={form.funding_year} onChange={(e) => set("funding_year", e.target.value)} className="rounded-none" placeholder="e.g. 2026" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Category</label>
                <Select value={form.category_of_service} onValueChange={(v) => set("category_of_service", v)}>
                  <SelectTrigger data-testid="form470-category-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="category_1">Category 1</SelectItem><SelectItem value="category_2">Category 2</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Services Requested</label>
              <textarea data-testid="form470-services-input" value={form.services_requested} onChange={(e) => set("services_requested", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Posting Date</label>
                <Input data-testid="form470-posting-input" type="date" value={form.posting_date} onChange={(e) => set("posting_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Contract Date</label>
                <Input data-testid="form470-contract-input" type="date" value={form.allowable_contract_date} onChange={(e) => set("allowable_contract_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="form470-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="open">Open</SelectItem><SelectItem value="closed">Closed</SelectItem><SelectItem value="certified">Certified</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="form470-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-form470-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-form470-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
